import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { 
  TrashIcon,
  EyeIcon,
  ChevronDownIcon,
  CheckIcon,
  ArrowPathIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';
import { api } from '../services/api';
import toast from '../utils/toast';

const ConnectionsTable = ({ 
  clients, 
  agents = [], 
  onClientUpdate, 
  onClientDelete, 
  showDeleteAction = true,
  maxRows = null,
  showMobilePort = false 
}) => {
  const [reassigning, setReassigning] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [agentFilter, setAgentFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Helper function to get display identifier for a client
  const getClientDisplayInfo = (client) => {
    const hasWhatsAppNumber = client.whatsapp_number && client.whatsapp_number.trim() !== '';
    const displayId = client.client_id; // Always use client_id as main text
    const subtitle = hasWhatsAppNumber ? `${client.whatsapp_number}` : client.email;
    return { displayId, subtitle };
  };

  // Filtered and searched clients
  const filteredClients = useMemo(() => {
    let filtered = clients;

    // Apply search filter
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(client => {
        const { displayId } = getClientDisplayInfo(client);
        return (
          client.client_id.toLowerCase().includes(search) ||
          displayId.toLowerCase().includes(search) ||
          client.email.toLowerCase().includes(search) ||
          (client.agent_name && client.agent_name.toLowerCase().includes(search)) ||
          (client.whatsapp_number && client.whatsapp_number.includes(search))
        );
      });
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(client => client.status === statusFilter);
    }

    // Apply agent filter
    if (agentFilter !== 'all') {
      filtered = filtered.filter(client => client.agent_id === agentFilter);
    }

    return filtered;
  }, [clients, searchTerm, statusFilter, agentFilter]);

  // Get unique statuses and agents for filter options
  const uniqueStatuses = useMemo(() => {
    const statuses = [...new Set(clients.map(client => client.status))];
    return statuses.filter(Boolean);
  }, [clients]);

  const uniqueAgents = useMemo(() => {
    const agentMap = new Map();
    clients.forEach(client => {
      if (client.agent_id && client.agent_name) {
        agentMap.set(client.agent_id, client.agent_name);
      }
    });
    return Array.from(agentMap.entries()).map(([id, name]) => ({ id, name }));
  }, [clients]);

  const handleAgentReassignment = async (clientId, newAgentId, newAgentName) => {
    if (!newAgentId) return;
    
    try {
      setReassigning(clientId);
      
      // Update the client's agent assignment
      await api.updateClient(clientId, {
        agent_id: newAgentId
      });
      
      // Notify parent component of the update
      if (onClientUpdate) {
        onClientUpdate(clientId, { agent_id: newAgentId, agent_name: newAgentName });
      }
      
      toast.success(
        <div>
          <div className="font-medium">Agent Reassigned Successfully</div>
          <div className="text-sm opacity-90">Connection now uses {newAgentName}</div>
        </div>,
        {
          duration: 4000,
          icon: '🔄'
        }
      );
    } catch (error) {
      console.error('Error reassigning agent:', error);
      const errorMessage = error.response?.data?.error || 'Failed to reassign agent';
      toast.error(
        <div>
          <div className="font-medium">Reassignment Failed</div>
          <div className="text-sm opacity-90">{errorMessage}</div>
        </div>
      );
    } finally {
      setReassigning(null);
    }
  };

  const handleDeleteClient = async (clientId) => {
    if (!window.confirm(`Are you sure you want to delete client ${clientId}?`)) {
      return;
    }

    try {
      setDeleting(clientId);
      await api.deleteClient(clientId);
      toast.success(`Connection ${clientId} deleted successfully`);
      
      // Notify parent component of the deletion
      if (onClientDelete) {
        onClientDelete(clientId);
      }
    } catch (error) {
      console.error('Error deleting client:', error);
      toast.error('Failed to delete client');
    } finally {
      setDeleting(null);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
      case 'running':
        return <span className="status-running">Running</span>;
      case 'pending':
        return <span className="status-pending">Pending</span>;
      case 'stopped':
      case 'inactive':
        return <span className="status-stopped">Stopped</span>;
      case 'deleted':
        return <span className="status-error">Deleted</span>;
      default:
        return <span className="status-error">Unknown</span>;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Agent Selector Component - Redesigned for better UX
  const AgentSelector = ({ client, isCompact = false }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const currentAgent = agents.find(agent => agent.id === client.agent_id);
    
    const handleAgentSelect = (agent) => {
      if (agent.id !== client.agent_id) {
        handleAgentReassignment(client.client_id, agent.id, agent.name);
      }
      setIsModalOpen(false);
      setSearchTerm('');
    };
    
    const isReassigningThis = reassigning === client.client_id;
    
    // Filter agents based on search term - show all by default, filter only when searching
    const filteredAgents = searchTerm.trim() 
      ? agents.filter(agent => 
          agent.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (agent.description && agent.description.toLowerCase().includes(searchTerm.toLowerCase()))
        )
      : agents; // Show all agents when no search term
    
    // If no agents available, just show a link to the agent
    if (agents.length === 0) {
      return (
        <Link
          to={`/dashboard/agents/${client.agent_id}`}
          className="text-whatsapp-600 hover:text-whatsapp-700 font-medium"
        >
          {client.agent_name || 'Unknown Agent'}
        </Link>
      );
    }
    
    return (
      <>
        <div className="relative inline-block text-left agent-selector">
          <button
            onClick={() => setIsModalOpen(true)}
            disabled={isReassigningThis}
            className={`inline-flex items-center justify-between ${
              isCompact ? 'w-full' : 'min-w-[140px]'
            } px-3 py-2 text-sm font-medium text-whatsapp-600 hover:text-whatsapp-700 bg-whatsapp-50 hover:bg-whatsapp-100 rounded-lg border border-whatsapp-200 transition-all duration-200 hover:shadow-sm hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-none`}
          >
            <div className="flex items-center truncate">
              <div className="w-2 h-2 bg-whatsapp-500 rounded-full mr-2 flex-shrink-0"></div>
              <span className="truncate">
                {isReassigningThis ? 'Reassigning...' : (currentAgent?.name || client.agent_name || 'Unknown Agent')}
              </span>
            </div>
            {!isReassigningThis && (
              <ChevronDownIcon className="ml-2 h-4 w-4 flex-shrink-0" />
            )}
          </button>
        </div>
        
        {/* Modal Overlay */}
        {isModalOpen && (
          <div className="fixed inset-0 z-50 overflow-y-auto agent-selector-modal">
            <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              {/* Background overlay */}
              <div 
                className="modal-backdrop fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
                onClick={() => {
                  setIsModalOpen(false);
                  setSearchTerm('');
                }}
              ></div>
              
              {/* Modal panel */}
              <div className="modal-panel inline-block align-bottom bg-white rounded-xl px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
                <div>
                  <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-whatsapp-100">
                    <ArrowPathIcon className="h-6 w-6 text-whatsapp-600" />
                  </div>
                  <div className="mt-3 text-center sm:mt-5">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Reassign Agent
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Select a new AI agent for connection <span className="font-medium">{client.client_id}</span>
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Search input */}
                <div className="mt-4">
                  <div className="relative">
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Filter agents by name or description..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Escape') {
                          setIsModalOpen(false);
                          setSearchTerm('');
                        }
                      }}
                      className="pl-10 pr-4 py-3 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-whatsapp-500 focus:border-whatsapp-500 text-sm transition-all duration-200 focus:scale-[1.01]"
                      autoFocus
                    />
                  </div>
                  {searchTerm ? (
                    <div className="mt-2 text-xs text-gray-500 flex items-center justify-between">
                      <span>{filteredAgents.length} agent{filteredAgents.length !== 1 ? 's' : ''} found</span>
                      {filteredAgents.length === 0 && (
                        <button
                          onClick={() => setSearchTerm('')}
                          className="text-whatsapp-600 hover:text-whatsapp-700 font-medium"
                        >
                          Clear search
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="mt-2 text-xs text-gray-500">
                      {filteredAgents.length} agent{filteredAgents.length !== 1 ? 's' : ''} available
                    </div>
                  )}
                </div>
                
                {/* Agents list */}
                <div className="mt-4">
                  <div className="h-80 overflow-y-auto border border-gray-200 rounded-lg">
                    {filteredAgents.length === 0 ? (
                      <div className="p-6 text-center">
                        {searchTerm ? (
                          <>
                            <MagnifyingGlassIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                            <h3 className="text-sm font-medium text-gray-900 mb-2">No agents found</h3>
                            <p className="text-sm text-gray-500 mb-4">No agents match "{searchTerm}"</p>
                            <button
                              onClick={() => setSearchTerm('')}
                              className="text-whatsapp-600 hover:text-whatsapp-700 font-medium text-sm"
                            >
                              Clear search to see all agents
                            </button>
                          </>
                        ) : (
                          <>
                            <ArrowPathIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                            <h3 className="text-sm font-medium text-gray-900 mb-2">No agents available</h3>
                            <p className="text-sm text-gray-500">Create an agent first to reassign this connection</p>
                          </>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-0 divide-y divide-gray-200">
                        {filteredAgents.map(agent => (
                          <button
                            key={agent.id}
                            onClick={() => handleAgentSelect(agent)}
                            className={`w-full text-left p-4 hover:bg-gray-50 transition-all duration-200 hover:translate-x-1 block ${
                              agent.id === client.agent_id 
                                ? 'bg-whatsapp-50 border-l-4 border-whatsapp-500' 
                                : 'hover:border-l-4 hover:border-gray-200'
                            }`}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-start">
                                  <div className={`w-3 h-3 rounded-full mr-3 mt-1 flex-shrink-0 ${
                                    agent.status === 'active' ? 'bg-green-500 animate-pulse' : 
                                    agent.status === 'inactive' ? 'bg-gray-400' : 'bg-yellow-500'
                                  }`}></div>
                                  <div className="flex-1 min-w-0">
                                    <div className="font-medium text-gray-900 text-base truncate">{agent.name}</div>
                                    {agent.description && (
                                      <div className="text-sm text-gray-500 mt-1 line-clamp-2">{agent.description}</div>
                                    )}
                                    <div className="flex flex-wrap items-center mt-2 gap-2">
                                      <span className="px-2 py-1 bg-gray-100 rounded-full text-xs">Model: {agent.model_name || 'Default'}</span>
                                      {agent.knowledge_base_enabled && (
                                        <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs">Knowledge Base</span>
                                      )}
                                      <span className="px-2 py-1 bg-gray-100 rounded-full text-xs capitalize">{agent.status}</span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              {agent.id === client.agent_id && (
                                <div className="flex items-center text-whatsapp-600 ml-3 flex-shrink-0">
                                  <CheckIcon className="h-5 w-5" />
                                  <span className="ml-1 text-sm font-medium">Current</span>
                                </div>
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Modal actions */}
                <div className="mt-5 sm:mt-6">
                  <button
                    type="button"
                    className="w-full inline-flex justify-center rounded-lg border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-whatsapp-500 sm:text-sm"
                    onClick={() => {
                      setIsModalOpen(false);
                      setSearchTerm('');
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </>
    );
  };

  // Note: Modal approach doesn't need click-outside handler since overlay handles it

  const displayClients = maxRows ? filteredClients.slice(0, maxRows) : filteredClients;
  
  // Add pagination logic
  const totalPages = Math.ceil(filteredClients.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedClients = maxRows ? filteredClients.slice(0, maxRows) : filteredClients.slice(startIndex, startIndex + itemsPerPage);
  
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  return (
    <div className="space-y-4">
      {/* Search and Filter Controls - Only show if not limited by maxRows */}
      {!maxRows && (
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search Input */}
            <div className="flex-1">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search connections, WhatsApp numbers, emails, or agents..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:ring-whatsapp-500 focus:border-whatsapp-500"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>

            {/* Filter Toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`inline-flex items-center px-4 py-2 border rounded-md text-sm font-medium transition-colors ${
                showFilters || statusFilter !== 'all' || agentFilter !== 'all'
                  ? 'border-whatsapp-500 text-whatsapp-700 bg-whatsapp-50'
                  : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
              }`}
            >
              <FunnelIcon className="h-4 w-4 mr-2" />
              Filters
              {(statusFilter !== 'all' || agentFilter !== 'all') && (
                <span className="ml-2 bg-whatsapp-500 text-white text-xs rounded-full px-2 py-0.5">
                  {[statusFilter !== 'all' ? 1 : 0, agentFilter !== 'all' ? 1 : 0].reduce((a, b) => a + b, 0)}
                </span>
              )}
            </button>
          </div>

          {/* Filter Options */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Status Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-whatsapp-500 focus:border-whatsapp-500"
                  >
                    <option value="all">All Statuses</option>
                    {uniqueStatuses.map(status => (
                      <option key={status} value={status}>
                        {status.charAt(0).toUpperCase() + status.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Agent Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">AI Agent</label>
                  <select
                    value={agentFilter}
                    onChange={(e) => setAgentFilter(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-whatsapp-500 focus:border-whatsapp-500"
                  >
                    <option value="all">All Agents</option>
                    {uniqueAgents.map(agent => (
                      <option key={agent.id} value={agent.id}>
                        {agent.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Clear Filters */}
              {(searchTerm || statusFilter !== 'all' || agentFilter !== 'all') && (
                <div className="mt-4 flex justify-end">
                  <button
                    onClick={() => {
                      setSearchTerm('');
                      setStatusFilter('all');
                      setAgentFilter('all');
                    }}
                    className="text-sm text-gray-600 hover:text-gray-800"
                  >
                    Clear all filters
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Results count */}
          <div className="mt-4 text-sm text-gray-600">
            Showing {startIndex + 1} - {Math.min(startIndex + itemsPerPage, filteredClients.length)} of {filteredClients.length} connections
          </div>
        </div>
      )}

      <div className="overflow-x-auto">
      {/* Desktop view */}
      <div className="hidden sm:block">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Connection
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                AI Agent
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Created At
              </th>
              <th className="relative px-6 py-3">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {paginatedClients.map((client) => {
              const { displayId, subtitle } = getClientDisplayInfo(client);
              return (
              <tr key={client.client_id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div>
                    <div className="text-sm font-medium text-gray-900">
                      {displayId}
                    </div>
                    <div className="text-sm text-gray-500">{subtitle}</div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {getStatusBadge(client.status)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  <AgentSelector client={client} />
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {formatDate(client.created_at)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                  <Link
                    to={`/dashboard/connections/${client.client_id}`}
                    className="text-whatsapp-600 hover:text-whatsapp-900 inline-flex items-center"
                  >
                    <EyeIcon className="h-4 w-4 mr-1" />
                    Connect
                  </Link>
                  {showDeleteAction && (
                    <button
                      onClick={() => handleDeleteClient(client.client_id)}
                      disabled={deleting === client.client_id}
                      className="text-red-600 hover:text-red-900 inline-flex items-center disabled:opacity-50"
                    >
                      {deleting === client.client_id ? (
                        <ArrowPathIcon className="h-4 w-4 mr-1 animate-spin" />
                      ) : (
                        <TrashIcon className="h-4 w-4 mr-1" />
                      )}
                      Delete
                    </button>
                  )}
                </td>
              </tr>
            );})}
          </tbody>
        </table>
      </div>
      
      {/* Mobile view - Card layout */}
      <div className="sm:hidden space-y-4">
        {paginatedClients.map((client) => {
          const { displayId, subtitle } = getClientDisplayInfo(client);
          return (
          <div key={client.client_id} className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-medium text-gray-900">
                {displayId}
              </div>
              {getStatusBadge(client.status)}
            </div>
            <div className="text-sm text-gray-500 mb-2">{subtitle}</div>
            <div className="text-sm text-gray-500 mb-3">
              <span className="font-medium">Created:</span> {formatDate(client.created_at)}
            </div>
            <div className="text-sm text-gray-500 mb-3">
              <span className="font-medium">AI Agent:</span>{' '}
              <div className="mt-1">
                <AgentSelector client={client} isCompact={true} />
              </div>
            </div>
            {showMobilePort && (
              <div className="text-sm text-gray-500 mb-3">
                <span className="font-medium">Port:</span> {client.port || 'N/A'}
              </div>
            )}
            <div className="flex justify-between items-center pt-2 border-t border-gray-100">
              <Link
                to={`/dashboard/connections/${client.client_id}`}
                className="text-whatsapp-600 hover:text-whatsapp-900 font-medium inline-flex items-center"
              >
                <EyeIcon className="h-4 w-4 mr-1" />
                Connect
              </Link>
              {showDeleteAction && (
                <button
                  onClick={() => handleDeleteClient(client.client_id)}
                  disabled={deleting === client.client_id}
                  className="text-red-600 hover:text-red-900 inline-flex items-center disabled:opacity-50"
                >
                  {deleting === client.client_id ? (
                    <ArrowPathIcon className="h-4 w-4 mr-1 animate-spin" />
                  ) : (
                    <TrashIcon className="h-4 w-4 mr-1" />
                  )}
                  Delete
                </button>
              )}
            </div>
          </div>
        );})}
      </div>
      
      {/* Pagination Controls - Only show if not limited by maxRows and there are more than itemsPerPage results */}
      {!maxRows && filteredClients.length > itemsPerPage && (
        <div className="bg-white px-6 py-4 border-t border-gray-200 sm:rounded-b-md">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Showing <span className="font-medium">{startIndex + 1}</span> to{' '}
              <span className="font-medium">
                {Math.min(startIndex + itemsPerPage, filteredClients.length)}
              </span>{' '}
              of <span className="font-medium">{filteredClients.length}</span> results
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className={`inline-flex items-center px-3 py-1 border border-gray-300 rounded-md text-sm font-medium ${
                  currentPage === 1
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                <ChevronLeftIcon className="h-4 w-4 mr-1" />
                Previous
              </button>
              <div className="flex items-center space-x-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`inline-flex items-center px-3 py-1 border text-sm font-medium rounded-md ${
                        currentPage === pageNum
                          ? 'bg-whatsapp-600 text-white border-whatsapp-600'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                {totalPages > 5 && (
                  <span className="inline-flex items-center px-2 py-1 text-sm text-gray-500">
                    ...
                  </span>
                )}
              </div>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`inline-flex items-center px-3 py-1 border border-gray-300 rounded-md text-sm font-medium ${
                  currentPage === totalPages
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                Next
                <ChevronRightIcon className="h-4 w-4 ml-1" />
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default ConnectionsTable;